import { useQuery } from '@apollo/client/react'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

/**
 * Type-safe runtime constants for the Buffer Home task key and status
 * enums. Mirrors the gql.tada-emitted string unions (which are
 * compile-time only) — `satisfies Record<string, ...>` makes any drift
 * between these constants and the GraphQL schema a TS error.
 */
export const BUFFER_HOME_TASK_KEY = {
  ConnectChannel: 'connectChannel',
  InviteTeam: 'inviteTeam',
  CreateFirstPost: 'createFirstPost',
  TryBufferApi: 'tryBufferApi',
} as const satisfies Record<string, BufferHomeTaskKey>

export const BUFFER_HOME_TASK_STATUS = {
  Pending: 'pending',
  Completed: 'completed',
  Dismissed: 'dismissed',
} as const satisfies Record<string, BufferHomeTaskStatus>

export const GetBufferHomeChecklist = graphql(/* GraphQL */ `
  query GetBufferHomeChecklist($input: BufferHomeChecklistInput!) {
    bufferHomeChecklist(input: $input) {
      tasks {
        taskKey
        status
      }
    }
  }
`)

type BufferHomeChecklistTask = ResultOf<
  typeof GetBufferHomeChecklist
>['bufferHomeChecklist']['tasks'][number]

/** A Buffer Home first-steps task key (e.g. `'connectChannel'`). */
export type BufferHomeTaskKey = BufferHomeChecklistTask['taskKey']

/** A Buffer Home first-steps task status, as returned by the checklist query. */
export type BufferHomeTaskStatus = BufferHomeChecklistTask['status']

export type UseBufferHomeChecklistResult = {
  tasks: ReadonlyArray<{
    taskKey: BufferHomeTaskKey
    status: BufferHomeTaskStatus
  }>
  loading: boolean
  organizationId: string | null
}

/**
 * Reads the per-org Buffer Home first-steps checklist. Only reached from the
 * Home page, which the `/home` route restricts to organization owners, so this
 * hook isn't run for other users.
 *
 * Returns an empty `tasks` list while the org id is not yet known or the
 * query is still in flight; consumers should check `loading` before
 * concluding that a task is genuinely pending.
 */
export function useBufferHomeChecklist(): UseBufferHomeChecklistResult {
  const organizationId = useOrganizationId()
  const { data, loading } = useQuery(GetBufferHomeChecklist, {
    variables: { input: { organizationId: organizationId ?? '' } },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
  })

  return {
    tasks: data?.bufferHomeChecklist.tasks ?? [],
    loading: !organizationId || (loading && data === undefined),
    organizationId: organizationId ?? null,
  }
}
